import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDemoContext } from '@/contexts/DemoContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Play, Mail, CheckCircle, Copy } from 'lucide-react';

const DemoLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDemoMode, validateToken, requestAccess, isLoading } = useDemoContext();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [magicLink, setMagicLink] = useState<string | null>(null);

  // If already in demo mode, redirect to hub
  useEffect(() => {
    if (isDemoMode && !isLoading) {
      navigate('/demo/hub', { replace: true });
    }
  }, [isDemoMode, isLoading, navigate]);

  // Auto-validate token from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token && !isDemoMode) {
      setValidating(true);
      validateToken(token).then((valid) => {
        setValidating(false);
        if (valid) {
          toast.success('Welcome to the RecruitLook demo!');
          navigate('/demo/hub', { replace: true });
        } else {
          toast.error('Invalid or expired demo link. Please request a new one.');
        }
      });
    }
  }, [searchParams, isDemoMode, validateToken, navigate]);

  const handleRequestAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubmitting(true);
    const result = await requestAccess(email, name, company);
    setSubmitting(false);

    if (result.success && result.magicLink) {
      setMagicLink(result.magicLink);
      toast.success('Demo access link generated!');
    } else {
      toast.error(result.error || 'Failed to generate demo access');
    }
  };

  const copyLink = () => {
    if (magicLink) {
      navigator.clipboard.writeText(magicLink);
      toast.success('Link copied to clipboard');
    }
  };

  if (isLoading || validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#002E62]">
        <div className="text-center text-white">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p className="text-lg">Validating demo access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#002E62] to-[#001a3a] p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 bg-[#BC8D09] rounded-full p-3 w-16 h-16 flex items-center justify-center">
            <Play className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Fjalla One, sans-serif' }}>
            RecruitLook Demo
          </CardTitle>
          <CardDescription>
            Experience the full event management platform. Enter your email to get instant access.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {magicLink ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-medium mb-2">Demo link ready!</p>
                <p className="text-green-600 text-sm mb-3">
                  Click below to enter the demo, or copy the link.
                </p>
              </div>
              <Button
                className="w-full bg-[#002E62] hover:bg-[#001a3a]"
                onClick={() => window.location.href = magicLink}
              >
                <Play className="w-4 h-4 mr-2" />
                Enter Demo Now
              </Button>
              <Button variant="outline" className="w-full" onClick={copyLink}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
          ) : (
            <form onSubmit={handleRequestAccess} className="space-y-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="company">Organization</Label>
                <Input
                  id="company"
                  placeholder="Your organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#BC8D09] hover:bg-[#a07b08] text-white"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Access...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Get Demo Access
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoLoginPage;
